/**
 * OfficeReportMatrix — matriz escritório × mês (heatmap) para uma métrica.
 */
import { MONTHS_PT, METRICS } from "@/lib/officeReport";

const fmt = (n) => (n || 0).toLocaleString("pt-PT");

export const monthLabel = (key, withYear = false) => {
  const [y, m] = key.split("-");
  const lbl = MONTHS_PT[Number(m) - 1];
  return withYear ? `${lbl} ${y.slice(2)}` : lbl;
};

export const spansYears = (months) => new Set((months || []).map((k) => k.slice(0, 4))).size > 1;

export default function OfficeReportMatrix({ data, metric, onMetricChange }) {
  const meta = METRICS.find((m) => m.key === metric) || METRICS[0];
  const withYear = spansYears(data.months);
  const max = data.offices.reduce(
    (acc, o) => o.months.reduce((a, b) => Math.max(a, b[metric] || 0), acc), 1
  );
  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="office-report-matrix">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-ui text-lg text-foreground">Matriz mensal por escritório</h3>
          <div className="text-xs text-muted-foreground">Intensidade da cor proporcional ao volume do mês</div>
        </div>
        <div className="flex items-center gap-1 text-[11px] flex-wrap" data-testid="office-report-metric-tabs">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => onMetricChange(m.key)}
              data-testid={`office-report-metric-${m.key}`}
              className={`px-3 py-1.5 rounded-full border transition-colors ${
                metric === m.key
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border/60">
        <table className="w-full text-sm" data-testid="office-report-matrix-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold bg-muted/30 border-b border-border">
              <th className="text-left py-2.5 px-3 sticky left-0 bg-muted/30">Escritório</th>
              {data.months.map((k) => (
                <th key={k} className="text-right py-2.5 px-2 whitespace-nowrap">{monthLabel(k, withYear)}</th>
              ))}
              <th className="text-right py-2.5 px-3 border-l border-border">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.offices.map((o) => (
              <tr key={o.office} className="border-b border-border/60 last:border-b-0" data-testid={`office-report-matrix-row-${o.office}`}>
                <td className="py-2 px-3 font-medium text-foreground whitespace-nowrap sticky left-0 bg-card">{o.office}</td>
                {o.months.map((b) => {
                  const v = b[metric] || 0;
                  const alpha = v ? 0.12 + 0.6 * (v / max) : 0;
                  return (
                    <td
                      key={b.key}
                      className="py-2 px-2 text-right tabular-nums"
                      style={{ backgroundColor: v ? `${meta.color}${Math.round(alpha * 255).toString(16).padStart(2, "0")}` : undefined }}
                      title={`${o.office} · ${monthLabel(b.key, true)} · ${meta.label}: ${v}`}
                    >
                      {v ? fmt(v) : <span className="text-muted-foreground/40">·</span>}
                    </td>
                  );
                })}
                <td className="py-2 px-3 text-right tabular-nums font-semibold border-l border-border" style={{ color: meta.color }}>
                  {fmt(o.totals[metric])}
                </td>
              </tr>
            ))}
            {data.offices.length > 0 && (
              <tr className="bg-muted/30 font-semibold" data-testid="office-report-matrix-total-row">
                <td className="py-2 px-3 sticky left-0 bg-muted/30">Total</td>
                {data.totals_by_month.map((b) => (
                  <td key={b.key} className="py-2 px-2 text-right tabular-nums">{fmt(b[metric])}</td>
                ))}
                <td className="py-2 px-3 text-right tabular-nums border-l border-border">{fmt(data.grand_totals[metric])}</td>
              </tr>
            )}
            {data.offices.length === 0 && (
              <tr><td colSpan={data.months.length + 2} className="py-6 text-center text-sm text-muted-foreground">Sem actividade no período.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
